import React, { useEffect, useState } from "react";
import styles from "./attendance.module.css";
import { Badge, Col, Row, Select, Typography } from "antd";
import CustomButton from "../../../../../../common/custom-button/custom-button";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import CustomAttendanceCalendar from "../../../../../../components/custom-attendance-calendar/custom-attendance-calendar";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from "recharts";
import CustomSelect from "../../../../../../common/input/custom-select/custom-select";
import {
  fetchStudentAttendance,
  getStudentAttendanceForExport,
} from "../../../../../../services/student";
import { toast } from "react-toastify";

const { Option } = Select;
const Attendance = ({ studentID, classroomID }) => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totalDays, setTotalDays] = useState(0);

  const COLORS = ["#72E181", "#E92C2C", "#0085FF", "#F8CF6F"];

  useEffect(() => {
    studentID && getStudentAttendance();
  }, [studentID]);

  useEffect(() => {
    if (window.innerWidth < 400) {
      setTimeout(() => {
        document.querySelector(".rbc-toolbar button.rbc-active")?.click();
      }, 300);
    }
  }, []);

  const getStudentAttendance = async () => {
    try {
      setLoading(true);
      const payload = {
        classroom_id: classroomID,
        student_id: studentID,
      };
      const response = await fetchStudentAttendance(payload);
      if (response?.data) {
        formatChartData(response?.data?.data?.header_stats);
        formatAttendanceData(response?.data?.data?.records);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const formatChartData = (data) => {
    const tempData = [
      { name: "School Attended", value: data?.school_attended ?? 0 },
      { name: "Absent", value: data?.absent ?? 0 },
      { name: "School Holiday", value: data?.school_holidays ?? 0 },
      { name: "Weekly Off", value: data?.weekly_offs ?? 0 },
    ];
    setTotalDays(data?.total_days ?? 0);
    setChartData(tempData);
  };

  const formatAttendanceData = (data) => {
    const tempData = data?.length
      ? data.map((e) => {
          return {
            title: e?.is_present ? "Present" : "Absent",
            allDay: true,
            start: e?.date,
            end: e?.date,
            component: (
              <span>
                {e?.is_present ? (
                  <CheckCircleOutlined
                    style={{ color: "green", fontSize: "24px" }}
                  />
                ) : (
                  <CloseCircleOutlined
                    style={{ color: "red", fontSize: "24px" }}
                  />
                )}
              </span>
            ),
          };
        })
      : [];

    setAttendanceData(tempData);
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const response = await getStudentAttendanceForExport(
        classroomID,
        studentID
      );

      if (response?.status) {
        setLoading(false);
        const blob = new Blob([response?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `attendance history.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to export file.");
    }
  };

  const renderCustomLegend = (props) => {
    const { payload } = props;
    return (
      <div className={styles.legendContainer}>
        <div className={styles.legendRow}>
          <Typography className={styles.legendHeaderTitle}>
            Total Days
          </Typography>
          <Typography className={styles.legendHeaderValue}>
            {totalDays} days
          </Typography>
        </div>
        {payload.map((entry, index) => (
          <div className={styles.legendRow} key={index}>
            <Badge color={entry.color} text={entry.value} />
            <Typography className={styles.legendValue}>
              {entry.payload.value} days
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.attendance}>
      <div className={styles.headerContainer}>
        <Typography className={styles.headerTitle}>Attendance</Typography>

        <CustomButton
          title="Export Attendance CSV"
          style={{
            background: "rgba(57, 181, 74, 0.10)",
            border: "1px solid #F5F5F5",
            color: "#39B54A",
            fontWeight: 500,
            fontSize: "14px",
          }}
          onClick={handleExportCSV}
          iconPosition={"end"}
          icon={<img src="/assets/images/export-icon.svg" alt="mail" />}
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <CustomAttendanceCalendar
            eventData={attendanceData}
            views={["month", "week"]}
            height={500}
            defaultView="month"
          />
        </Col>
        <Col xs={24} md={8} lg={8}>
          <div className={styles.chartContainer}>
            <div className={styles.filterContainer}>
              <Typography>Overall Attendance</Typography>
              <CustomSelect id={"year"} placeholder={"Select"} value={"2025"}>
                <Option value="2025">2025</Option>
                <Option value="2024">2024</Option>
              </CustomSelect>
            </div>
            <PieChart width={300} height={300}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                height={36}
                content={renderCustomLegend}
              />
            </PieChart>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Attendance;
